/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {css} from '@emotion/core'
import {getTheme, getThemeStyles} from '../util/theme'
import {getLanguageCode} from '../util/locale'
import smoothScroll from '../util/smoothScroll'

import Layout from '../components/Layout'

import termsOfServiceData from '../translations/termsOfServiceData.json'
import subscribersEmailLight from '../images/subscribers-email-light.png'
import subscribersEmailDark from '../images/subscribers-email-dark.png'

// Rendered as an image (not text) so the address can't be scraped
const EMAIL_IMAGE_MARKER = '%EMAIL_IMAGE%'

const TermsOfService = ({languageCode}) => {
    const lang = languageCode || getLanguageCode()

    const {seo, pageTitle, intro, contentsTitle, sections} = termsOfServiceData[lang]

    const [theme, setTheme] = useState('light')
    const [themeStyles, setThemeStyles] = useState({})
    const [activeId, setActiveId] = useState('')

    const HASH_ROUTE_PATTERN = /#[\w-]+/g
    const getScrollToId = () => {
        if (typeof window === `undefined` && typeof document === `undefined`) {
            return null
        }
        const matched = window.location.href.match(HASH_ROUTE_PATTERN)
        if (matched) return matched[0]
        return ''
    }

    // id should include a # before it
    const scrollToEl = (id) => {
        return (e) => {
            if (e) e.preventDefault()
            const el = document.querySelector(`div[data-scroll-id='${id}']`)
            if (el) {
                setActiveId(id)
                if (typeof window !== `undefined` && typeof document !== 'undefined') {
                    if (window.history.pushState) {
                        window.history.pushState(null, null, id)
                    } else {
                        window.location.hash = id
                    }
                }
                smoothScroll(el, 130)
            }
        }
    }

    const scrollToElOnLoad = () => {
        if (typeof window === `undefined` && typeof document === `undefined`) {
            return null
        }
        const locationHash = getScrollToId()
        scrollToEl(locationHash)()
    }

    useEffect(() => {
        const t = getTheme()
        const tStyles = getThemeStyles()
        setTheme(t)
        setThemeStyles(tStyles)
    }, [theme, themeStyles])

    useEffect(() => {
        scrollToElOnLoad()
    }, [scrollToElOnLoad])

    return (
        <Layout
            title={seo.title}
            description={seo.description}
            lang={lang}
            canonicalPath="/terms-of-service/"
            themeStyles={themeStyles}
            theme={theme}
        >
            <section
                css={css`
                    padding: 10rem 2rem 4rem;
                    background: ${themeStyles.privacyBackground};
                    h1,
                    h2,
                    h3,
                    h4,
                    p,
                    strong,
                    a,
                    b,
                    em,
                    span,
                    ul,
                    li {
                        color: ${themeStyles.primaryTextColor};
                    }
                    a,
                    a span {
                        color: ${themeStyles.emphasisTextColor};
                    }
                `}
            >
                <div
                    className="inner"
                    css={css`
                        margin: auto;
                        display: flex;
                        max-width: 1200px;
                    `}
                >
                    <div
                        className="contents"
                        css={css`
                            flex: 1;
                            border-radius: 4px;
                            box-shadow: 0px 10px 20px rgba(32, 41, 69, 0.1);
                            height: 100%;
                            padding: 0;
                            margin: 1rem 2.5rem 0 0;
                            max-width: 315px;
                            position: -webkit-sticky;
                            position: sticky;
                            top: 125px;
                            background: ${themeStyles.lighterBackgroundColor};
                            h2 {
                                color: ${themeStyles.emphasisTextColor};
                                font-size: 30px;
                                font-weight: 600;
                                line-height: 32px;
                                position: absolute;
                                z-index: 3;
                                top: -1rem;
                                left: 1rem;
                            }
                            @media (max-width: 990px) {
                                display: none;
                            }
                        `}
                    >
                        <h2>{contentsTitle}</h2>
                        <div
                            css={css`
                                padding: 1.5rem 0.5rem 1rem 0.5rem;
                                background: ${themeStyles.lighterBackgroundColor};
                                border-radius: 4px;
                                position: relative;
                                z-index: 2;
                            `}
                        >
                            {sections.map(({title}, i) => {
                                const id = `#section-${i + 1}`
                                return (
                                    <div
                                        key={id}
                                        css={css`
                                            &:not(:last-of-type) {
                                                margin-bottom: 8px;
                                            }
                                        `}
                                    >
                                        <div
                                            css={css`
                                                ${id === activeId
                                                    ? `
                                                            background: ${themeStyles.privacyTabHighlight};
                                                            span {
                                                                font-weight: 600;
                                                                color: ${themeStyles.emphasisTextColor};
                                                            }
                                                        `
                                                    : ``}
                                                padding: 4px 8px;
                                                border-radius: 4px;
                                                font-size: 1rem;
                                                line-height: 24px;
                                                &:hover {
                                                    cursor: pointer;
                                                    span {
                                                        color: ${themeStyles.emphasisTextColor};
                                                    }
                                                }
                                                &:focus {
                                                    outline: none;
                                                }
                                            `}
                                            onClick={scrollToEl(id)}
                                            onKeyUp={scrollToEl(id)}
                                            role="tab"
                                            aria-selected="true"
                                            aria-controls="tabpanel-id"
                                            id="tab-id"
                                            tabIndex="0"
                                        >
                                            <span>{title}</span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        <div
                            css={css`
                                width: 40px;
                                height: 40px;
                                position: absolute;
                                z-index: 3;
                                right: 30px;
                                top: -30px;
                                background-color: ${themeStyles.privacyBoxBackground};
                                border: solid 1px ${themeStyles.privacyBoxBorder};
                            `}
                        />
                        <div
                            css={css`
                                width: 64px;
                                height: 64px;
                                position: absolute;
                                z-index: 1;
                                left: -40px;
                                bottom: -40px;
                                background-color: transparent;
                                border: solid 2px ${themeStyles.brandBlue};
                            `}
                        />
                    </div>
                    <div
                        className="terms-of-service"
                        css={css`
                            flex: 1;
                            h1 {
                                font-size: 30px;
                                font-weight: 600;
                                line-height: 42px;
                                margin: 0 0 1.5rem;
                            }
                            h3 {
                                margin: 2.5rem 0 1rem;
                                font-size: 24px;
                                font-weight: 600;
                                line-height: 32px;
                            }
                            h4 {
                                margin: 2rem 0 1rem;
                                font-size: 18px;
                                font-weight: 600;
                                line-height: 26px;
                            }
                            p,
                            li {
                                line-height: 24px;
                                margin: 0 0 1rem;
                                font-size: 16px;
                            }
                            ul {
                                padding: 0 0 0 1.5rem;
                            }
                            li {
                                &::marker {
                                    color: ${themeStyles.emphasisTextColor};
                                }
                            }
                            a {
                                color: ${themeStyles.emphasisTextColor};
                                text-decoration: none;
                            }
                        `}
                    >
                        <h1>{pageTitle}</h1>
                        {intro.map((para, i) => (
                            // eslint-disable-next-line react/no-danger
                            <p key={i} dangerouslySetInnerHTML={{__html: para}} />
                        ))}
                        {sections.map((section, i) => {
                            const id = `section-${i + 1}`
                            return (
                                <div data-scroll-id={`#${id}`} key={i} className="terms-of-service-item">
                                    <h3>{section.title}</h3>
                                    {(section.body || []).map((para, j) =>
                                        para === EMAIL_IMAGE_MARKER ? (
                                            <img
                                                key={j}
                                                src={
                                                    theme === 'light' || theme === 'air'
                                                        ? subscribersEmailLight
                                                        : subscribersEmailDark
                                                }
                                                alt="Contact email address"
                                                css={css`
                                                    display: block;
                                                    height: 24px;
                                                    width: auto;
                                                    margin: 0 0 1rem;
                                                `}
                                            />
                                        ) : (
                                            // eslint-disable-next-line react/no-danger
                                            <p key={j} dangerouslySetInnerHTML={{__html: para}} />
                                        ),
                                    )}
                                    {(section.subsections || []).map((sub, j) => (
                                        <div key={j}>
                                            <h4>{sub.title}</h4>
                                            {(sub.body || []).map((para, k) => (
                                                // eslint-disable-next-line react/no-danger
                                                <p key={k} dangerouslySetInnerHTML={{__html: para}} />
                                            ))}
                                            {sub.bullets && (
                                                <ul>
                                                    {sub.bullets.map((bullet, k) => (
                                                        // eslint-disable-next-line react/no-danger
                                                        <li key={k} dangerouslySetInnerHTML={{__html: bullet}} />
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>
        </Layout>
    )
}

TermsOfService.propTypes = {
    languageCode: PropTypes.string,
}

export default TermsOfService
